import { useEffect, useState } from "react";
import type { ImportPreviewOrg } from "../../utils/bpaImport";

export const PICK_WAIT_MS = 300;

const NO_ORGS: ImportPreviewOrg[] = [];

type OrganisationsDisplayInput = {
  sourceId: string | null;
  /** The list for the picked source has arrived (or failed). */
  listReady: boolean;
  loading: boolean;
  /** The firms the query holds for the picked source, or null when it holds none. */
  data: ImportPreviewOrg[] | null;
  /** `data` is the same source's earlier list, shown while a new one loads. */
  reloading: boolean;
};

function usePickWaitOver(sourceId: string | null): boolean {
  const [waitedFor, setWaitedFor] = useState<string | null>(null);
  useEffect(() => {
    if (sourceId === null) return;
    const timer = window.setTimeout(() => setWaitedFor(sourceId), PICK_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [sourceId]);
  return sourceId !== null && waitedFor === sourceId;
}

/**
 * Decides when the Organisations section opens and what it shows around a source pick. It opens
 * once the list for the picked source is ready, or PICK_WAIT_MS after the pick, whichever is
 * first. If it was already open, the earlier source's list stays (busy) for those PICK_WAIT_MS,
 * then the loading state replaces it.
 */
export function useOrganisationsDisplay(input: OrganisationsDisplayInput) {
  const { sourceId, listReady, loading, data, reloading } = input;
  const waitOver = usePickWaitOver(sourceId);
  const [open, setOpen] = useState(false);
  const [lastList, setLastList] = useState<ImportPreviewOrg[] | null>(null);

  if (!open && sourceId !== null && (listReady || waitOver)) setOpen(true);
  if (listReady && data && data.length > 0 && lastList !== data) setLastList(data);

  if (data) return { open, loading: false, reloading, orgs: data };
  if (loading && !waitOver && lastList)
    return { open, loading: false, reloading: true, orgs: lastList };
  return { open, loading, reloading: false, orgs: NO_ORGS };
}
